// src/pages/TranslateViewerPage.js
// Phase 3: Translated content viewer with language picker + side-by-side toggle
// Redesigned to match HousingAI homepage aesthetic
//
// Routes:
//   /translate/:org/:lang         — browse all translated pages
//   /translate/:org/:lang?url=... — view specific translated page

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Building2, FileText, Globe, BookOpen, AlertTriangle,
  Volume2, Play, Pause, SkipBack, SkipForward, X,
} from 'lucide-react';
import Brand from '../components/Brand';
import { trackTranslateDocView, trackReadAloudAvailability, trackReadAloudStart } from '../utils/analytics';
import useReadAloud, { splitForSpeech, detectPlatform, READ_ALOUD_RATES } from '../hooks/useReadAloud';


// ─── Config ──────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_BASE || 'https://housing-external.onrender.com';

const LANG_TO_COUNTRY = {
  en: 'us', es: 'es', zh: 'cn', vi: 'vn',
  ru: 'ru', ar: 'sa', ht: 'ht', pt: 'br', fr: 'fr',
  it: 'it', el: 'gr',
};

function FlagImg({ lang, size = 20 }) {
  const c = LANG_TO_COUNTRY[lang] || 'us';
  return (
    <img
      src={`https://flagcdn.com/w40/${c}.png`}
      srcSet={`https://flagcdn.com/w80/${c}.png 2x`}
      alt=""
      width={size}
      height={Math.round(size * 0.75)}
      style={{ objectFit: 'cover', borderRadius: 2, verticalAlign: 'middle', flexShrink: 0 }}
    />
  );
}

const LANGUAGE_NAMES = {
  en: 'English', es: 'Español', zh: '简体中文', vi: 'Tiếng Việt',
  ru: 'Русский', ar: 'العربية', ht: 'Kreyòl Ayisyen',
  pt: 'Português', fr: 'Français',
  it: 'Italiano', el: 'Ελληνικά',
};

// A document the housing authority published in this language itself (the
// Spanish CHAMP next to the English one). Served as-is, never machine-translated.
const OFFICIAL_LABELS = {
  en: 'Official Translation', es: 'Traducción Oficial', zh: '官方翻译',
  vi: 'Bản Dịch Chính Thức', ru: 'Официальный Перевод', ar: 'ترجمة رسمية',
  ht: 'Tradiksyon Ofisyèl', pt: 'Tradução Oficial', fr: 'Traduction Officielle',
  it: 'Traduzione Ufficiale', el: 'Επίσημη Μετάφραση',
};
const officialLabel = (lang) => OFFICIAL_LABELS[lang] || OFFICIAL_LABELS.en;

// ─── Design tokens (matching homepage) ──────────────────────────
const T = {
  blue600: '#2563eb',
  blue700: '#1d4ed8',
  blue50: '#eff6ff',
  blue100: '#dbeafe',
  purple50: '#faf5ff',
  gray900: '#111827',
  gray800: '#1f2937',
  gray700: '#374151',
  gray600: '#4b5563',
  gray500: '#5f6673', // darkened for 4.5:1 on light backgrounds (WCAG 1.4.3),
  gray400: '#6b7280', // darkened for 4.5:1 on white (WCAG 1.4.3),
  gray200: '#e5e7eb',
  gray100: '#f3f4f6',
  gray50: '#f9fafb',
  amber50: '#fffbeb',
  amber200: '#fde68a',
  amber700: '#b45309',
  amber800: '#92400e',
  amber900: '#78350f',
  white: '#ffffff',
  navy: '#1a2b4a',
  radius: '16px',
  radiusSm: '12px',
  radiusPill: '999px',
  shadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
  shadowSm: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
  gradient: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #faf5ff 100%)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontSerif: "'Source Serif 4', Georgia, 'Times New Roman', serif",
};

// ─── UI label translations ──────────────────────────────────────
const UI_LABELS = {
  en: {
    aiTranslation: 'AI Translation',
    documentsAvailable: 'Documents Available',
    websitePages: 'Website Pages',
    documentsForms: 'Documents & Forms',
    sideBySide: 'Side-by-Side',
    sideBySideOn: 'Side-by-Side ON',
    viewOriginal: 'View original on housing authority website',
    englishVersion: 'English (AI Transcribed)',
    aiVersion: 'AI Translation',
    importantNotice: 'Important Notice',
    officialEnglish: 'View Original Document',
    poweredBy: 'Powered by',
    translationsMayContain: 'AI-extracted text may contain inaccuracies',
    lastUpdated: 'Last updated',
    backToAll: 'Back to all pages',
    loading: 'Loading translation…',
    errorTitle: 'Unable to load translation',
    goBack: 'Go Back',
    housingAuthority: 'Housing Authority',
    contentDisclaimer: 'English text was extracted using AI and may contain errors. For official information, refer to the original document.',
  },
  es: {
    aiTranslation: 'Traducción IA',
    documentsAvailable: 'Documentos Traducidos Disponibles',
    websitePages: 'Páginas del Sitio Web',
    documentsForms: 'Documentos y Formularios',
    sideBySide: 'Lado a Lado',
    sideBySideOn: 'Lado a Lado ACTIVO',
    viewOriginal: 'Ver original (inglés)',
    englishVersion: 'Inglés (Transcrito por IA)',
    aiVersion: 'Traducción IA',
    importantNotice: 'Aviso Importante',
    officialEnglish: 'Ver Documento Original en Inglés',
    poweredBy: 'Desarrollado por',
    translationsMayContain: 'Las traducciones de IA pueden contener errores',
    lastUpdated: 'Última actualización',
    backToAll: 'Volver a todas las páginas',
    loading: 'Cargando traducción…',
    errorTitle: 'No se pudo cargar la traducción',
    goBack: 'Volver',
    housingAuthority: 'Autoridad de Vivienda',
    contentDisclaimer: 'Las versiones en inglés y traducida fueron generadas por IA y pueden contener errores.',
  },
  fr: {
    aiTranslation: 'Traduction IA',
    documentsAvailable: 'Documents Traduits Disponibles',
    websitePages: 'Pages du Site Web',
    documentsForms: 'Documents et Formulaires',
    sideBySide: 'Côte à Côte',
    sideBySideOn: 'Côte à Côte ACTIF',
    viewOriginal: 'Voir l\'original (anglais)',
    englishVersion: 'Anglais (Transcrit par IA)',
    aiVersion: 'Traduction IA',
    importantNotice: 'Avis Important',
    officialEnglish: 'Voir le Document Original en Anglais',
    poweredBy: 'Propulsé par',
    translationsMayContain: 'Les traductions IA peuvent contenir des erreurs',
    lastUpdated: 'Dernière mise à jour',
    backToAll: 'Retour à toutes les pages',
    loading: 'Chargement de la traduction…',
    errorTitle: 'Impossible de charger la traduction',
    goBack: 'Retour',
    housingAuthority: 'Office du Logement',
    contentDisclaimer: 'Les versions anglaise et traduite sont générées par IA et peuvent contenir des erreurs.',
  },
  zh: {
    aiTranslation: 'AI翻译',
    documentsAvailable: '已翻译文件',
    websitePages: '网站页面',
    documentsForms: '文件和表格',
    sideBySide: '并排显示',
    sideBySideOn: '并排显示 开启',
    viewOriginal: '查看原文（英语）',
    englishVersion: '英文（AI转录）',
    aiVersion: 'AI翻译',
    importantNotice: '重要通知',
    officialEnglish: '查看英文原始文件',
    poweredBy: '技术支持',
    translationsMayContain: 'AI翻译可能包含错误',
    lastUpdated: '最后更新',
    backToAll: '返回所有页面',
    loading: '正在加载翻译…',
    errorTitle: '无法加载翻译',
    goBack: '返回',
    housingAuthority: '住房管理局',
    contentDisclaimer: '英文版和翻译版均由AI生成，可能包含错误。',
  },
  vi: {
    aiTranslation: 'Bản Dịch AI',
    documentsAvailable: 'Tài Liệu Đã Dịch',
    websitePages: 'Trang Web',
    documentsForms: 'Tài Liệu và Biểu Mẫu',
    sideBySide: 'Song Song',
    sideBySideOn: 'Song Song BẬT',
    viewOriginal: 'Xem bản gốc (tiếng Anh)',
    englishVersion: 'Tiếng Anh (AI Chép lại)',
    aiVersion: 'Bản Dịch AI',
    importantNotice: 'Thông Báo Quan Trọng',
    officialEnglish: 'Xem Tài Liệu Gốc Tiếng Anh',
    poweredBy: 'Được hỗ trợ bởi',
    translationsMayContain: 'Bản dịch AI có thể chứa lỗi',
    lastUpdated: 'Cập nhật lần cuối',
    backToAll: 'Quay lại tất cả trang',
    loading: 'Đang tải bản dịch…',
    errorTitle: 'Không thể tải bản dịch',
    goBack: 'Quay Lại',
    housingAuthority: 'Cơ Quan Nhà Ở',
    contentDisclaimer: 'Cả phiên bản tiếng Anh và bản dịch đều do AI tạo ra và có thể chứa lỗi.',
  },
  ru: {
    aiTranslation: 'ИИ Перевод',
    documentsAvailable: 'Переведённые Документы',
    websitePages: 'Страницы Сайта',
    documentsForms: 'Документы и Формы',
    sideBySide: 'Бок о Бок',
    sideBySideOn: 'Бок о Бок ВКЛ',
    viewOriginal: 'Посмотреть оригинал (английский)',
    englishVersion: 'Английский (ИИ Транскрипция)',
    aiVersion: 'ИИ Перевод',
    importantNotice: 'Важное Уведомление',
    officialEnglish: 'Посмотреть Оригинальный Документ на Английском',
    poweredBy: 'Разработано',
    translationsMayContain: 'ИИ переводы могут содержать ошибки',
    lastUpdated: 'Последнее обновление',
    backToAll: 'Вернуться ко всем страницам',
    loading: 'Загрузка перевода…',
    errorTitle: 'Не удалось загрузить перевод',
    goBack: 'Назад',
    housingAuthority: 'Жилищное Управление',
    contentDisclaimer: 'Английская и переведённая версии созданы ИИ и могут содержать ошибки.',
  },
  ar: {
    aiTranslation: 'ترجمة ذكاء اصطناعي',
    documentsAvailable: 'المستندات المترجمة المتاحة',
    websitePages: 'صفحات الموقع',
    documentsForms: 'المستندات والنماذج',
    sideBySide: 'جنبًا إلى جنب',
    sideBySideOn: 'جنبًا إلى جنب مُفعّل',
    viewOriginal: 'عرض الأصل (إنجليزي)',
    englishVersion: 'الإنجليزية (نسخ بالذكاء الاصطناعي)',
    aiVersion: 'ترجمة ذكاء اصطناعي',
    importantNotice: 'إشعار مهم',
    officialEnglish: 'عرض المستند الأصلي بالإنجليزية',
    poweredBy: 'مدعوم من',
    translationsMayContain: 'قد تحتوي ترجمات الذكاء الاصطناعي على أخطاء',
    lastUpdated: 'آخر تحديث',
    backToAll: 'العودة إلى جميع الصفحات',
    loading: '...جارٍ تحميل الترجمة',
    errorTitle: 'تعذر تحميل الترجمة',
    goBack: 'رجوع',
    housingAuthority: 'هيئة الإسكان',
    contentDisclaimer: 'النسختان الإنجليزية والمترجمة تم إنشاؤهما بالذكاء الاصطناعي وقد تحتويان على أخطاء.',
  },
  ht: {
    aiTranslation: 'Tradiksyon IA',
    documentsAvailable: 'Dokiman Tradui Disponib',
    websitePages: 'Paj Sit Entènèt',
    documentsForms: 'Dokiman ak Fòmilè',
    sideBySide: 'Kote pa Kote',
    sideBySideOn: 'Kote pa Kote AKTIVE',
    viewOriginal: 'Gade orijinal (angle)',
    englishVersion: 'Angle (Transkri pa IA)',
    aiVersion: 'Tradiksyon IA',
    importantNotice: 'Avi Enpòtan',
    officialEnglish: 'Gade Dokiman Orijinal an Angle',
    poweredBy: 'Sipòte pa',
    translationsMayContain: 'Tradiksyon IA ka gen erè',
    lastUpdated: 'Dènye mizajou',
    backToAll: 'Retounen nan tout paj yo',
    loading: 'Chajman tradiksyon…',
    errorTitle: 'Pa kapab chaje tradiksyon',
    goBack: 'Retounen',
    housingAuthority: 'Otorite Lojman',
    contentDisclaimer: 'Vèsyon angle ak tradiksyon yo te fèt pa IA epi yo ka gen erè.',
  },
  pt: {
    aiTranslation: 'Tradução IA',
    documentsAvailable: 'Documentos Traduzidos Disponíveis',
    websitePages: 'Páginas do Site',
    documentsForms: 'Documentos e Formulários',
    sideBySide: 'Lado a Lado',
    sideBySideOn: 'Lado a Lado ATIVO',
    viewOriginal: 'Ver original (inglês)',
    englishVersion: 'Inglês (Transcrito por IA)',
    aiVersion: 'Tradução IA',
    importantNotice: 'Aviso Importante',
    officialEnglish: 'Ver Documento Original em Inglês',
    poweredBy: 'Desenvolvido por',
    translationsMayContain: 'Traduções de IA podem conter erros',
    lastUpdated: 'Última atualização',
    backToAll: 'Voltar para todas as páginas',
    loading: 'Carregando tradução…',
    errorTitle: 'Não foi possível carregar a tradução',
    goBack: 'Voltar',
    housingAuthority: 'Autoridade Habitacional',
    contentDisclaimer: 'As versões em inglês e traduzida foram geradas por IA e podem conter erros.',
  },
  it: {
    aiTranslation: 'Traduzione IA',
    documentsAvailable: 'Documenti Tradotti Disponibili',
    websitePages: 'Pagine del Sito Web',
    documentsForms: 'Documenti e Moduli',
    sideBySide: 'Affiancato',
    sideBySideOn: 'Affiancato ATTIVO',
    viewOriginal: 'Vedi originale (inglese)',
    englishVersion: 'Inglese (Trascritto da IA)',
    aiVersion: 'Traduzione IA',
    importantNotice: 'Avviso Importante',
    officialEnglish: 'Vedi Documento Originale in Inglese',
    poweredBy: 'Sviluppato da',
    translationsMayContain: 'Le traduzioni IA possono contenere errori',
    lastUpdated: 'Ultimo aggiornamento',
    backToAll: 'Torna a tutte le pagine',
    loading: 'Caricamento traduzione…',
    errorTitle: 'Impossibile caricare la traduzione',
    goBack: 'Indietro',
    housingAuthority: 'Ente per l\'Edilizia Abitativa',
    contentDisclaimer: 'Le versioni inglese e tradotta sono generate dall\'IA e possono contenere errori.',
  },
  el: {
    aiTranslation: 'Μετάφραση AI',
    documentsAvailable: 'Μεταφρασμένα Έγγραφα Διαθέσιμα',
    websitePages: 'Σελίδες Ιστοτόπου',
    documentsForms: 'Έγγραφα και Φόρμες',
    sideBySide: 'Δίπλα-Δίπλα',
    sideBySideOn: 'Δίπλα-Δίπλα ΕΝΕΡΓΟ',
    viewOriginal: 'Δείτε το πρωτότυπο (αγγλικά)',
    englishVersion: 'Αγγλικά (Μεταγραφή AI)',
    aiVersion: 'Μετάφραση AI',
    importantNotice: 'Σημαντική Ειδοποίηση',
    officialEnglish: 'Δείτε το Πρωτότυπο Αγγλικό Έγγραφο',
    poweredBy: 'Υποστηρίζεται από',
    translationsMayContain: 'Οι μεταφράσεις AI μπορεί να περιέχουν σφάλματα',
    lastUpdated: 'Τελευταία ενημέρωση',
    backToAll: 'Επιστροφή σε όλες τις σελίδες',
    loading: 'Φόρτωση μετάφρασης…',
    errorTitle: 'Αδυναμία φόρτωσης μετάφρασης',
    goBack: 'Πίσω',
    housingAuthority: 'Οργανισμός Στέγασης',
    contentDisclaimer: 'Οι εκδόσεις στα αγγλικά και η μετάφραση δημιουργήθηκαν από AI και μπορεί να περιέχουν σφάλματα.',
  },
};

// ─── Disclaimer translations ────────────────────────────────────
const DISCLAIMERS = {
  en: 'All English text was extracted using AI and may not be perfectly accurate. For official information, please refer to the original document on the housing authority\'s website.',
  es: 'Esta traducción fue generada por inteligencia artificial y no es un documento oficial. Para información oficial, consulte la versión original en inglés en el sitio web de la autoridad de vivienda.',
  zh: '此翻译由人工智能生成，不是官方文件。如需官方信息，请参阅住房管理局网站上的英文原始版本。',
  vi: 'Bản dịch này được tạo bởi AI và không phải là tài liệu chính thức. Để biết thông tin chính thức, vui lòng tham khảo phiên bản tiếng Anh gốc trên trang web của cơ quan nhà ở.',
  ru: 'Этот перевод создан с помощью ИИ и не является официальным документом. Для получения официальной информации обратитесь к оригинальной английской версии на сайте жилищного управления.',
  ar: 'تمت هذه الترجمة بواسطة الذكاء الاصطناعي وهي ليست وثيقة رسمية. للحصول على المعلومات الرسمية، يرجى الرجوع إلى النسخة الإنجليزية الأصلية على موقع هيئة الإسكان.',
  ht: 'Tradiksyon sa a te fèt pa entèlijans atifisyèl epi li pa yon dokiman ofisyèl. Pou enfòmasyon ofisyèl, tanpri gade vèsyon orijinal angle a sou sit entènèt otorite lojman an.',
  pt: 'Esta tradução foi gerada por inteligência artificial e não é um documento oficial. Para informações oficiais, consulte a versão original em inglês no site da autoridade habitacional.',
  fr: 'Cette traduction a été générée par l\'intelligence artificielle et n\'est pas un document officiel. Pour les informations officielles, veuillez consulter la version originale en anglais sur le site de l\'office du logement.',
  it: 'Questa traduzione è stata generata dall\'intelligenza artificiale e non è un documento ufficiale. Per informazioni ufficiali, consultare la versione originale in inglese sul sito dell\'ente per l\'edilizia abitativa.',
  el: 'Αυτή η μετάφραση δημιουργήθηκε από τεχνητή νοημοσύνη και δεν αποτελεί επίσημο έγγραφο. Για επίσημες πληροφορίες, ανατρέξτε στην πρωτότυπη αγγλική έκδοση στον ιστότοπο του Οργανισμού Στέγασης.',
};

// ─── Helpers ─────────────────────────────────────────────────────
function getLabels(lang) {
  return UI_LABELS[lang] || UI_LABELS.en;
}

// ─── Read-aloud labels ───────────────────────────────────────────
const READ_ALOUD_LABELS = {
  en: {
    listen: 'Listen', play: 'Play', resume: 'Resume', pause: 'Pause', prev: 'Previous paragraph', next: 'Next paragraph',
    close: 'Stop reading', speed: 'Reading speed', tapHint: 'Tap any paragraph to read from there.',
    noVoice: 'No voice for this language on this device.',
    noVoiceWindows: 'Microsoft Edge includes voices for this language, or add one in Windows Settings › Time & language › Speech.',
    noVoiceAndroid: 'On Android: open Settings and search for “text-to-speech”. Set the preferred engine to Google’s speech services, install the voice data for this language, then restart Chrome.',
    voiceError: 'Reading stopped. The voice is not available right now.',
  },
  es: {
    listen: 'Escuchar', play: 'Reproducir', resume: 'Reanudar', pause: 'Pausa', prev: 'Párrafo anterior', next: 'Párrafo siguiente',
    close: 'Dejar de leer', speed: 'Velocidad de lectura', tapHint: 'Toque cualquier párrafo para leer desde ahí.',
    noVoice: 'No hay una voz para este idioma en este dispositivo.',
    noVoiceWindows: 'Microsoft Edge incluye voces para este idioma, o puede agregar una en Configuración de Windows › Hora e idioma › Voz.',
    noVoiceAndroid: 'En Android: abra Configuración y busque «texto a voz». Elija el motor de voz de Google como motor preferido, instale los datos de voz de este idioma y reinicie Chrome.',
    voiceError: 'La lectura se detuvo. La voz no está disponible en este momento.',
  },
  zh: {
    listen: '朗读', play: '播放', resume: '继续', pause: '暂停', prev: '上一段', next: '下一段',
    close: '停止朗读', speed: '朗读速度', tapHint: '点击任意段落，从该处开始朗读。',
    noVoice: '此设备上没有该语言的语音。',
    noVoiceWindows: 'Microsoft Edge 自带该语言的语音，您也可以在 Windows 设置 › 时间和语言 › 语音 中添加。',
    noVoiceAndroid: '安卓手机：打开“设置”，搜索“文字转语音”。将首选引擎设为 Google 语音服务，安装该语言的语音数据，然后重新启动 Chrome。',
    voiceError: '朗读已停止，该语音当前不可用。',
  },
  vi: {
    listen: 'Nghe', play: 'Phát', resume: 'Tiếp tục', pause: 'Tạm dừng', prev: 'Đoạn trước', next: 'Đoạn sau',
    close: 'Dừng đọc', speed: 'Tốc độ đọc', tapHint: 'Chạm vào bất kỳ đoạn nào để đọc từ đó.',
    noVoice: 'Thiết bị này không có giọng đọc cho ngôn ngữ này.',
    noVoiceWindows: 'Microsoft Edge có sẵn giọng đọc cho ngôn ngữ này, hoặc bạn có thể thêm trong Cài đặt Windows › Thời gian và ngôn ngữ › Giọng nói.',
    noVoiceAndroid: 'Trên Android: mở Cài đặt và tìm “chuyển văn bản thành giọng nói”. Chọn công cụ giọng nói của Google làm công cụ ưu tiên, cài đặt dữ liệu giọng nói cho ngôn ngữ này rồi khởi động lại Chrome.',
    voiceError: 'Đã dừng đọc. Giọng đọc hiện không khả dụng.',
  },
  ru: {
    listen: 'Слушать', play: 'Воспроизвести', resume: 'Продолжить', pause: 'Пауза', prev: 'Предыдущий абзац', next: 'Следующий абзац',
    close: 'Остановить чтение', speed: 'Скорость чтения', tapHint: 'Нажмите на любой абзац, чтобы читать с него.',
    noVoice: 'На этом устройстве нет голоса для этого языка.',
    noVoiceWindows: 'В Microsoft Edge есть голоса для этого языка, или добавьте голос в Параметры Windows › Время и язык › Речь.',
    noVoiceAndroid: 'На Android: откройте Настройки и найдите «синтез речи». Выберите движок Google в качестве основного, установите голосовые данные для этого языка и перезапустите Chrome.',
    voiceError: 'Чтение остановлено. Голос сейчас недоступен.',
  },
  ar: {
    listen: 'استمع', play: 'تشغيل', resume: 'استئناف', pause: 'إيقاف مؤقت', prev: 'الفقرة السابقة', next: 'الفقرة التالية',
    close: 'إيقاف القراءة', speed: 'سرعة القراءة', tapHint: 'اضغط على أي فقرة للقراءة منها.',
    noVoice: 'لا يوجد صوت لهذه اللغة على هذا الجهاز.',
    noVoiceWindows: 'يتضمن Microsoft Edge أصواتًا لهذه اللغة، أو يمكنك إضافة صوت من إعدادات Windows › الوقت واللغة › الكلام.',
    noVoiceAndroid: 'على Android: افتح الإعدادات وابحث عن «تحويل النص إلى كلام». اختر محرك الكلام من Google كمحرك مفضل، ثم ثبّت بيانات الصوت لهذه اللغة وأعد تشغيل Chrome.',
    voiceError: 'توقفت القراءة. الصوت غير متاح حاليًا.',
  },
  ht: {
    listen: 'Koute', play: 'Jwe', resume: 'Kontinye', pause: 'Poz', prev: 'Paragraf anvan', next: 'Paragraf apre',
    close: 'Sispann li', speed: 'Vitès lekti', tapHint: 'Tape nenpòt paragraf pou li apati la.',
    noVoice: 'Pa gen vwa pou lang sa a sou aparèy sa a.',
    noVoiceWindows: 'Microsoft Edge gen vwa pou lang sa a, oswa ajoute youn nan Paramèt Windows › Lè ak lang › Pawòl.',
    noVoiceAndroid: 'Sou Android: louvri Paramèt epi chèche “text-to-speech”. Chwazi motè vwa Google la, enstale done vwa pou lang sa a, epi rekòmanse Chrome.',
    voiceError: 'Lekti a sispann. Vwa a pa disponib kounye a.',
  },
  pt: {
    listen: 'Ouvir', play: 'Reproduzir', resume: 'Retomar', pause: 'Pausar', prev: 'Parágrafo anterior', next: 'Próximo parágrafo',
    close: 'Parar leitura', speed: 'Velocidade de leitura', tapHint: 'Toque em qualquer parágrafo para ler a partir dali.',
    noVoice: 'Não há voz para este idioma neste dispositivo.',
    noVoiceWindows: 'O Microsoft Edge inclui vozes para este idioma, ou adicione uma em Configurações do Windows › Hora e idioma › Fala.',
    noVoiceAndroid: 'No Android: abra Configurações e pesquise “texto em voz”. Escolha o mecanismo de voz do Google como preferido, instale os dados de voz deste idioma e reinicie o Chrome.',
    voiceError: 'A leitura parou. A voz não está disponível no momento.',
  },
  fr: {
    listen: 'Écouter', play: 'Lire', resume: 'Reprendre', pause: 'Pause', prev: 'Paragraphe précédent', next: 'Paragraphe suivant',
    close: 'Arrêter la lecture', speed: 'Vitesse de lecture', tapHint: 'Touchez un paragraphe pour lire à partir de là.',
    noVoice: 'Aucune voix pour cette langue sur cet appareil.',
    noVoiceWindows: 'Microsoft Edge inclut des voix pour cette langue, ou ajoutez-en une dans Paramètres Windows › Heure et langue › Voix.',
    noVoiceAndroid: 'Sur Android : ouvrez Paramètres et recherchez « synthèse vocale ». Choisissez le moteur vocal de Google comme moteur préféré, installez les données vocales de cette langue, puis redémarrez Chrome.',
    voiceError: 'Lecture arrêtée. La voix n\'est pas disponible pour le moment.',
  },
  it: {
    listen: 'Ascolta', play: 'Riproduci', resume: 'Riprendi', pause: 'Pausa', prev: 'Paragrafo precedente', next: 'Paragrafo successivo',
    close: 'Interrompi lettura', speed: 'Velocità di lettura', tapHint: 'Tocca un paragrafo per leggere da lì.',
    noVoice: 'Nessuna voce per questa lingua su questo dispositivo.',
    noVoiceWindows: 'Microsoft Edge include voci per questa lingua, oppure aggiungine una in Impostazioni di Windows › Data/ora e lingua › Voce.',
    noVoiceAndroid: 'Su Android: apri Impostazioni e cerca “sintesi vocale”. Scegli il motore vocale di Google come motore preferito, installa i dati vocali per questa lingua e riavvia Chrome.',
    voiceError: 'Lettura interrotta. La voce non è disponibile al momento.',
  },
  el: {
    listen: 'Ακρόαση', play: 'Αναπαραγωγή', resume: 'Συνέχεια', pause: 'Παύση', prev: 'Προηγούμενη παράγραφος', next: 'Επόμενη παράγραφος',
    close: 'Διακοπή ανάγνωσης', speed: 'Ταχύτητα ανάγνωσης', tapHint: 'Πατήστε σε οποιαδήποτε παράγραφο για ανάγνωση από εκεί.',
    noVoice: 'Δεν υπάρχει φωνή για αυτή τη γλώσσα σε αυτή τη συσκευή.',
    noVoiceWindows: 'Το Microsoft Edge περιλαμβάνει φωνές για αυτή τη γλώσσα ή προσθέστε μία από Ρυθμίσεις Windows › Ώρα και γλώσσα › Ομιλία.',
    noVoiceAndroid: 'Σε Android: ανοίξτε τις Ρυθμίσεις και αναζητήστε «μετατροπή κειμένου σε ομιλία». Επιλέξτε τη μηχανή ομιλίας της Google ως προτιμώμενη, εγκαταστήστε τα δεδομένα φωνής για αυτή τη γλώσσα και επανεκκινήστε το Chrome.',
    voiceError: 'Η ανάγνωση σταμάτησε. Η φωνή δεν είναι διαθέσιμη αυτή τη στιγμή.',
  },
};

function formatOrgName(org) {
  return org.charAt(0).toUpperCase() + org.slice(1);
}

// ─── Responsive hook ─────────────────────────────────────────────
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

// ─── Scroll To Top Button ────────────────────────────────────────
// raised: lifts the button clear of the read-aloud bar while that is open.
function ScrollToTopButton({ raised = false }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
      style={{
        position: 'fixed',
        bottom: raised ? '6.5rem' : '1.5rem',
        right: '1.5rem',
        width: '42px',
        height: '42px',
        borderRadius: '50%',
        border: `1px solid ${T.gray200}`,
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        boxShadow: T.shadow,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 40,
        transition: 'opacity 0.2s ease, transform 0.2s ease',
        fontSize: '1.1rem',
        color: T.gray600,
        fontWeight: 700,
      }}
      onMouseOver={e => { e.currentTarget.style.background = T.blue50; e.currentTarget.style.color = T.blue600; e.currentTarget.style.borderColor = T.blue600; }}
      onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; e.currentTarget.style.color = T.gray600; e.currentTarget.style.borderColor = T.gray200; }}
    >
      ↑
    </button>
  );
}

// ─── FormattedText ──────────────────────────────────────────────
// ─── Markdown-table helpers ─────────────────────────────────────
function ft_isSeparatorRow(line) {
  const t = line.trim();
  if (!t.includes('-') || !t.includes('|')) return false;
  return /^\|?[\s:|-]+\|?$/.test(t) && /-/.test(t);
}
function ft_looksLikeTableRow(line) {
  const t = line.trim();
  if (!t.includes('|')) return false;
  const pipes = (t.match(/\|/g) || []).length;
  return pipes >= 2 || /^\|/.test(t) || /\|$/.test(t);
}
function ft_splitRow(line) {
  let t = line.trim();
  if (t.startsWith('|')) t = t.slice(1);
  if (t.endsWith('|')) t = t.slice(0, -1);
  return t.split('|').map(c => c.trim());
}
function ft_nextNonBlank(lines, from) {
  let k = from;
  while (k < lines.length && lines[k].trim() === '') k++;
  return k;
}
// Split full text into ordered segments: {type:'table'} | {type:'text'}.
// Tolerant of blank lines the cleanup pass may insert between table rows.
function ft_segment(text) {
  const lines = text.split('\n');
  const segs = [];
  let i = 0;
  const startsTable = (idx) => {
    if (!ft_looksLikeTableRow(lines[idx])) return false;
    const j = ft_nextNonBlank(lines, idx + 1);
    return j < lines.length && ft_isSeparatorRow(lines[j]);
  };

  while (i < lines.length) {
    if (startsTable(i)) {
      const header = ft_splitRow(lines[i]);
      const sep = ft_nextNonBlank(lines, i + 1);
      i = sep + 1;
      const rows = [];
      while (true) {
        const k = ft_nextNonBlank(lines, i);
        if (k >= lines.length || !ft_looksLikeTableRow(lines[k])) break;
        // Multi-page tables repeat [header][separator] at each page break. Skip a
        // stray separator (page-break continuation) and any repeated header row
        // (a row immediately followed by a separator), so the table stays as ONE.
        if (ft_isSeparatorRow(lines[k])) { i = k + 1; continue; }
        const k2 = ft_nextNonBlank(lines, k + 1);
        if (k2 < lines.length && ft_isSeparatorRow(lines[k2])) { i = k + 1; continue; }
        rows.push(ft_splitRow(lines[k]));
        i = k + 1;
      }
      // Normalize ragged rows to a common width. Widen to the WIDEST row rather
      // than slicing to the header: a checkbox grid that came back as a 1-cell
      // header over 3-cell rows would otherwise silently lose two thirds of
      // its options. Never drop a cell; pad short rows and the header instead.
      const cols = Math.max(header.length, ...rows.map(r => r.length));
      const fullHeader = header.slice();
      while (fullHeader.length < cols) fullHeader.push('');
      const norm = rows.map(r => {
        const c = r.slice();
        while (c.length < cols) c.push('');
        return c;
      });
      segs.push({ type: 'table', header: fullHeader, rows: norm });
    } else {
      const buf = [];
      while (i < lines.length && !startsTable(i)) { buf.push(lines[i]); i++; }
      const joined = buf.join('\n');
      if (joined.trim()) segs.push({ type: 'text', text: joined });
    }
  }
  return segs;
}

function MarkdownTable({ header, rows, mobile }) {
  const cellBase = {
    border: `1px solid ${T.gray200}`,
    padding: mobile ? '0.4rem 0.5rem' : '0.55rem 0.7rem',
    fontSize: mobile ? '0.78rem' : '0.85rem',
    lineHeight: 1.4,
    verticalAlign: 'top',
    textAlign: 'left',
    minWidth: mobile ? '70px' : '90px',
  };
  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', margin: '1rem 0', border: `1px solid ${T.gray200}`, borderRadius: T.radiusSm }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: header.length > 4 ? '640px' : 'auto', fontFamily: T.font }}>
        <thead>
          <tr>
            {header.map((h, j) => (
              <th key={j} style={{ ...cellBase, background: T.gray100, color: T.gray900, fontWeight: 700, whiteSpace: 'normal' }}>
                {h ? ft_inline(h, `h${j}`) : '\u00A0'}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} style={{ background: ri % 2 ? T.gray50 : T.white }}>
              {r.map((c, ci) => (
                <td key={ci} style={{ ...cellBase, color: T.gray700 }}>
                  {c ? ft_inline(c, `c${ri}-${ci}`) : '\u00A0'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Inline markdown: bold, italic, links (D1) ───────────────────
// Emphasis comes from real markers in the extracted text, never from guessing.
// A bare or unpaired asterisk ("Public Housing***", a leading "*Note") is real
// document content and stays literal — see D6.

// Apply one regex across a mixed array of strings/elements, wrapping matches.
function ft_applyRule(nodes, re, make) {
  const out = [];
  nodes.forEach((node, ni) => {
    if (typeof node !== 'string') { out.push(node); return; }
    let last = 0;
    let m;
    re.lastIndex = 0;
    while ((m = re.exec(node)) !== null) {
      if (m.index > last) out.push(node.slice(last, m.index));
      out.push(make(m, `${ni}-${m.index}`));
      last = m.index + m[0].length;
      if (re.lastIndex === m.index) re.lastIndex++; // guard against zero-length
    }
    if (last < node.length) out.push(node.slice(last));
  });
  return out;
}

function ft_inline(text, keyPrefix, depth = 0) {
  if (!text) return null;
  let nodes = [text];
  // Parse the inside of a bold/underline span too, two levels deep at most.
  const inner = (s, k) => (depth < 2 ? ft_inline(s, k, depth + 1) : s);

  // **bold** and <u>underline</u> in ONE pass so either can nest inside the
  // other: forms carry both **<u>x</u>** and <u>**x**</u>. The leftmost match
  // wins and its contents are parsed recursively. Only the literal <u> and
  // </u> tokens are recognized (D3); any other tag stays inert text, so
  // document content can never inject markup.
  nodes = ft_applyRule(nodes, /\*\*(?!\s)([^\n]*?[^\s*])\*\*|<u>([^<\n]{1,500}?)<\/u>/g,
    (m, k) => (m[1] !== undefined
      ? <strong key={`${keyPrefix}-b${k}`}>{inner(m[1], `${keyPrefix}-b${k}`)}</strong>
      : <u key={`${keyPrefix}-u${k}`}>{inner(m[2], `${keyPrefix}-u${k}`)}</u>));

  // _italic_ — a tight pair that does not touch another underscore, so a
  // fill-in blank (____) and adjacent blanks (__ City __) stay literal. The
  // preceding character is captured and re-emitted (no lookbehind, for Safari).
  nodes = ft_applyRule(nodes, /(^|[^_\w])_(?!\s)([^_\n]*?[^\s_])_(?![_\w])/g,
    (m, k) => (
      <React.Fragment key={`${keyPrefix}-i${k}`}>
        {m[1]}<em>{m[2]}</em>
      </React.Fragment>
    ));

  // *italic* — only a tight pair opening at a word boundary (D6: a bare or
  // unpaired asterisk is footnote content and stays literal).
  nodes = ft_applyRule(nodes, /(^|[\s(])\*(?!\s)([^*\n]{1,300}?[^\s*])\*(?![*\w])/g,
    (m, k) => (
      <React.Fragment key={`${keyPrefix}-j${k}`}>
        {m[1]}<em>{m[2]}</em>
      </React.Fragment>
    ));

  // Bare URLs -> clickable links (#3). Trailing punctuation is left outside.
  nodes = ft_applyRule(nodes, /(https?:\/\/|www\.)[^\s<>"'`)\]]*[^\s<>"'`)\].,;:!?]/g,
    (m, k) => (
      <a key={`${keyPrefix}-l${k}`}
         href={m[0].startsWith('www.') ? `https://${m[0]}` : m[0]}
         target="_blank" rel="noopener noreferrer"
         style={{ color: T.blue700, textDecoration: 'underline', wordBreak: 'break-word' }}>
        {m[0]}
      </a>
    ));

  // Graphic placeholders the extractor emits for non-text elements (D4):
  // [Map: ...], [Logo: ...], [Signature]. Shown muted, brackets kept, so a
  // reader sees that something was there without mistaking it for content.
  nodes = ft_applyRule(nodes, /\[(?:Map|Logo|Image|Photo|Seal|Signature|Figure|Chart|Diagram)\b[^\]\n]{0,200}\]/gi,
    (m, k) => (
      <span key={`${keyPrefix}-p${k}`} style={{ color: T.gray500, fontStyle: 'italic' }}>
        {m[0]}
      </span>
    ));

  return nodes;
}

// ─── Page furniture (D7) ─────────────────────────────────────────
// The raw string is preserved verbatim; we only render it muted. Footer line
// order varies across pages, so each line is matched independently and a block
// counts as furniture only when EVERY line in it does.
// Anchors are language-neutral: they read identically in every translation.
function ft_isFurnitureAnchor(line) {
  const t = line.trim();
  if (!t) return false;
  if (/^CHAMP\b/i.test(t) && /mass\.gov/i.test(t)) return true;     // CHAMP url line
  if (/^https?:\/\/(www\.)?mass\.gov\/champ\/?$/i.test(t)) return true; // url on its own line
  if (/^\d{1,2}\/\d{4}(\s.{0,24})?$/.test(t)) return true;          // "01/2026", "01/2026 Page 7"
  if (/^\[\s*blank space[^\]]*\]$/i.test(t)) return true;           // "[Blank Space]"
  return false;
}

// The page marker is TRANSLATED ("Page 1", "Pagina 1", "Seite 1", the Chinese,
// Arabic and Greek forms), so matching the English word left every non-English
// footer rendering as ordinary body text. Accept any SHORT line carrying a
// digit instead -- but only inside a block that already has a real anchor. That
// context requirement is what stops a stray short number elsewhere in the
// document from being greyed out.
function ft_isFurnitureCompanion(line) {
  const t = line.trim();
  // A graphic placeholder in the footer (the Equal Housing logo repeats on
  // every page) belongs to the footer block.
  if (/^\[(?:Logo|Image|Seal|Map)\b[^\]]{0,80}\]$/i.test(t)) return true;
  if (/^CHAMP$/i.test(t)) return true;                              // the word alone, url on the next line
  if (!t || t.length > 24) return false;
  if (!/\d/.test(t)) return false;
  if (/[.!?。！？]$/.test(t)) return false;
  return true;
}

// A page number standing alone in its own block: "Page 3", "Page 3 of 25",
// "Página 3 de 25", "3/25", "- 3 -", "第 3 页". One optional word before the
// number and at most one short word or separator after it, so a sentence
// that merely contains a number never matches.
function ft_isPageNumberLine(line) {
  const t = line.trim();
  if (!t || t.length > 20) return false;
  if (/^[-–—]\s*\d{1,3}\s*[-–—]$/.test(t)) return true;
  return /^(?:\p{L}{1,10}\.?\s*)?\d{1,3}(?:\s*(?:\p{L}{1,4}|\/|-)\s*\d{1,3}|\s*\p{L}{1,4})?\s*$/u.test(t);
}

function ft_isFurnitureBlock(block) {
  const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
  if (!lines.length) return false;
  if (lines.length === 1 && ft_isPageNumberLine(lines[0])) return true;
  if (!lines.some(ft_isFurnitureAnchor)) return false;
  return lines.every(l => ft_isFurnitureAnchor(l) || ft_isFurnitureCompanion(l));
}

const FT_HEADING_SIZES = { 1: '1.35rem', 2: '1.2rem', 3: '1.1rem', 4: '1rem', 5: '0.95rem', 6: '0.9rem' };
const FT_HARD_BREAK = '\u0000'; // sentinel marking a markdown hard line break
// Bullets: '-', '•', or '* ' (asterisk followed by a space). A bare '*' or
// '***' is footnote content, never a bullet — D6.
const FT_BULLET_RE = /^\s*(?:[-•]|\*(?=\s))\s*/;

// Non-table text segment. Structure is read from the data (markdown), not
// inferred from block length — the old isHeading heuristic bolded ~73% of
// Chinese blocks because CJK ends sentences with 。 rather than '.'.
function TextSegment({ text, contentType, startIndex }) {
  const paragraphs = text.split(/\n\n+/);
  return paragraphs.map((para, i) => {
    const trimmed = para.trim();
    if (!trimmed) return null;
    const key = `${startIndex}-${i}`;

    // Page furniture -> small muted footer line (D7).
    if (ft_isFurnitureBlock(trimmed)) {
      const fLines = trimmed.split('\n').map(l => l.trim()).filter(Boolean);
      return (
        <p key={key} style={{
          margin: '0.5rem 0',
          fontSize: '0.78rem',
          lineHeight: 1.5,
          color: T.gray500,
          textAlign: 'center',
        }}>
          {fLines.map((line, j) => (
            <React.Fragment key={j}>
              {line}
              {j < fLines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      );
    }

    // Real ATX heading from the document ("## Section"). A heading may share
    // its block with a hard-broken subtitle ("# CHAMP  \nCommon Housing
    // Application..."), so only the FIRST line is tested; any remaining lines
    // render as the paragraph that follows the heading.
    // The extractor sometimes emits a bare "#" (a heading marker with no
    // text) at the top of a page that starts mid-section. Drop it.
    if (/^#{1,6}$/.test(trimmed)) return null;

    const firstBreak = trimmed.indexOf('\n');
    const firstLine = firstBreak === -1 ? trimmed : trimmed.slice(0, firstBreak).trim();
    const h = firstLine.match(/^(#{1,6})\s+(.+)$/);
    if (h) {
      const level = h[1].length;
      const HTag = `h${Math.min(level + 1, 6)}`;
      const rest = firstBreak === -1 ? '' : trimmed.slice(firstBreak + 1).trim();
      // A document title ("# CHAMP" plus a one- or two-line subtitle) sits
      // centered on the printed page; section headings stay left-aligned.
      const centered = level === 1 && rest.split('\n').filter(l => l.trim()).length <= 2;
      return (
        <div key={key} style={centered ? { textAlign: 'center' } : undefined}>
          <HTag style={{
            fontSize: FT_HEADING_SIZES[level] || '1rem',
            fontWeight: 700,
            color: T.gray900,
            marginTop: '1.5rem',
            marginBottom: '0.5rem',
            fontFamily: T.fontSerif,
          }}>
            {ft_inline(h[2], key)}
          </HTag>
          {rest && <TextSegment text={rest} contentType={contentType} startIndex={`${key}-r`} />}
        </div>
      );
    }

    const bulletRe = FT_BULLET_RE;
    const lines = trimmed.split('\n').filter(l => l.trim());
    if (lines.length > 0 && lines.some(l => bulletRe.test(l))) {
      // Group consecutive lines into list runs and prose runs. A block that
      // mixes the two (a question followed by its options, or a list whose
      // continuation sentence has no dash) used to fall through to a plain
      // paragraph and show literal dashes.
      const runs = [];
      for (const line of lines) {
        const isB = bulletRe.test(line);
        const last = runs[runs.length - 1];
        if (last && last.isB === isB) last.lines.push(line);
        else runs.push({ isB, lines: [line] });
      }
      return (
        <React.Fragment key={key}>
          {runs.map((run, ri) => (run.isB ? (
            <ul key={ri} style={{
              margin: '0.75rem 0',
              paddingLeft: '1.5rem',
              listStyleType: 'disc',
            }}>
              {run.lines.map((line, j) => (
                <li key={j} style={{
                  marginBottom: '0.35rem',
                  lineHeight: 1.7,
                  color: T.gray700,
                }}>
                  {ft_inline(line.replace(bulletRe, ''), `${key}-${ri}-${j}`)}
                </li>
              ))}
            </ul>
          ) : (
            <p key={ri} style={{
              marginBottom: '0.85rem',
              lineHeight: 1.7,
              color: T.gray700,
            }}>
              {run.lines.map((line, j) => (
                <React.Fragment key={j}>
                  {ft_inline(line.replace(/[ \t]{2,}$/, '').trim(), `${key}-${ri}-${j}`)}
                  {j < run.lines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          )))}
        </React.Fragment>
      );
    }

    // Paragraph. A markdown hard break (two+ spaces before a newline) is a real
    // line break and 90%+ of documents use them; collapsing them was what merged
    // title blocks into one run-on line (#2). A bare newline is a soft wrap.
    let norm = trimmed.replace(/[ \t]{2,}\n/g, FT_HARD_BREAK);
    norm = contentType === 'html'
      ? norm.replace(/\n/g, FT_HARD_BREAK)
      : norm.replace(/\n/g, ' ');
    const outLines = norm.split(FT_HARD_BREAK);

    return (
      <p key={key} style={{
        marginBottom: '0.85rem',
        lineHeight: 1.7,
        color: T.gray700,
      }}>
        {outLines.map((line, j) => (
          <React.Fragment key={j}>
            {ft_inline(line, `${key}-${j}`)}
            {j < outLines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
  });
}

// Rendered block by block (the same split side-by-side and read-aloud use), so
// each paragraph can be highlighted or tapped while reading aloud.
function FormattedText({ text, className = '', contentType = 'pdf', reader = null }) {
  const mobile = useIsMobile();
  if (!text) return null;
  const blocks = ft_blocks(text);
  // Fill-in blanks are runs of up to ~100 underscores with no break
  // opportunity, so a phone treats each as one word and the line overflows
  // the document box. Let any run wrap; prose is unaffected.
  return (
    <div className={`formatted-text ${className}`} style={{ overflowWrap: 'anywhere' }}>
      {blocks.map((block, i) => (
        <ReadableBlock key={i} index={i} reader={reader}>
          {ft_renderBlock(block, contentType, mobile, i)}
        </ReadableBlock>
      ))}
    </div>
  );
}

// ─── Paired side-by-side (#11) ───────────────────────────────────
// Flatten a document into comparable blocks: a table stays one block, a text
// segment splits into paragraphs. English and translation are produced by the
// same pipeline and the translation prompt preserves blank lines, so block
// counts line up (CHAMP: 609 = 609).
function ft_blocks(text) {
  const out = [];
  if (!text) return out;
  for (const seg of ft_segment(text)) {
    if (seg.type === 'table') { out.push(seg); continue; }
    for (const para of seg.text.split(/\n\n+/)) {
      if (para.trim()) out.push({ type: 'text', text: para });
    }
  }
  return out;
}

// ─── Read-aloud text ─────────────────────────────────────────────
// What a voice should say: markdown markers, blanks, placeholders and page
// furniture removed, bare URLs reduced to their site name.
function ft_plain(s) {
  if (!s) return '';
  const t = s
    .replace(/^\s*#{1,6}\s+/, '')
    .replace(/<\/?u>/g, '')
    .replace(/\*\*/g, '')
    .replace(/(^|[^_\w])_(?!\s)([^_\n]*?[^\s_])_(?![_\w])/g, '$1$2')
    .replace(/(^|[\s(])\*(?!\s)([^*\n]{1,300}?[^\s*])\*(?![*\w])/g, '$1$2')
    .replace(/_{2,}/g, ' ')
    .replace(/\*/g, '')
    .replace(/\[(?:Map|Logo|Image|Photo|Seal|Signature|Figure|Chart|Diagram|\s*blank space)\b[^\]\n]{0,200}\]/gi, ' ')
    .replace(/(?:https?:\/\/|www\.)[^\s<>"'`)\]]*[^\s<>"'`)\].,;:!?]/g,
      (u) => u.replace(/^https?:\/\//, '').replace(/^www\./, '').split(/[/?#]/)[0])
    .replace(/[|☐☑☒□■◻◼▢✓✔✗✘]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return /[\p{L}\p{N}]/u.test(t) ? t : '';
}

// One entry per line the reader sees, mirroring TextSegment's structure, so a
// heading, list item or hard-broken line each get their own pause. Tables are
// read row by row as "Header: value, Header: value".
function ft_speechLines(block, contentType) {
  if (!block) return [];
  if (block.type === 'table') {
    const header = block.header.map(ft_plain);
    const out = block.rows.map(r => r.map((c, i) => {
      const v = ft_plain(c);
      if (!v) return '';
      return header[i] && header[i] !== v ? `${header[i]}: ${v}` : v;
    }).filter(Boolean).join(', ')).filter(Boolean);
    if (!out.length && header.some(Boolean)) out.push(header.filter(Boolean).join(', '));
    return out;
  }
  const text = block.text.trim();
  if (!text || /^#{1,6}$/.test(text) || ft_isFurnitureBlock(text)) return [];
  const out = [];
  let body = text;
  const firstBreak = body.indexOf('\n');
  const firstLine = firstBreak === -1 ? body : body.slice(0, firstBreak).trim();
  if (/^#{1,6}\s+/.test(firstLine)) {
    out.push(firstLine);
    body = firstBreak === -1 ? '' : body.slice(firstBreak + 1).trim();
  }
  const lines = body.split('\n').filter(l => l.trim());
  if (lines.some(l => FT_BULLET_RE.test(l))) {
    lines.forEach(l => out.push(l.replace(FT_BULLET_RE, '')));
  } else if (body) {
    let norm = body.replace(/[ \t]{2,}\n/g, FT_HARD_BREAK);
    norm = contentType === 'html' ? norm.replace(/\n/g, FT_HARD_BREAK) : norm.replace(/\n/g, ' ');
    out.push(...norm.split(FT_HARD_BREAK));
  }
  return out.map(ft_plain).filter(Boolean);
}

// Wraps one rendered block. While read-aloud is open, the block being read is
// highlighted and any speakable block can be tapped to read from there. No
// padding or border, so margins collapse exactly as they did unwrapped.
function ReadableBlock({ index, reader, children }) {
  if (!reader) return children;
  const speakable = !!reader.speakable[index];
  const isCurrent = reader.current === index;
  const pickable = reader.active && speakable;
  const onClick = pickable ? (e) => {
    if (e.target.closest('a')) return;                        // let links work
    const sel = window.getSelection && window.getSelection();
    if (sel && sel.toString().trim()) return;                 // selecting text, not picking
    reader.onPick(index);
  } : undefined;
  return (
    <div
      data-ra-block={index}
      onClick={onClick}
      aria-current={isCurrent ? 'true' : undefined}
      className={pickable ? 'ra-pickable' : undefined}
      style={isCurrent ? { background: T.blue100, boxShadow: `0 0 0 6px ${T.blue100}`, borderRadius: 4 } : undefined}
    >
      {children}
    </div>
  );
}

function ft_renderBlock(block, contentType, mobile, key) {
  if (!block) return null;
  return block.type === 'table'
    ? <MarkdownTable header={block.header} rows={block.rows} mobile={mobile} />
    : <TextSegment text={block.text} contentType={contentType} startIndex={key} />;
}

// One grid ROW per block pair, so the two languages cannot drift apart while
// scrolling — the row's height is the taller of the two cells. Previously each
// column was an independent flow, which is what caused the drift. If the block
// counts ever diverge, the shorter side simply leaves empty cells.
function PairedText({ leftText, rightText, contentType = 'pdf', isRTL = false, reader = null }) {
  const mobile = useIsMobile();
  const L = ft_blocks(leftText);
  const R = ft_blocks(rightText);
  const rowCount = Math.max(L.length, R.length);
  const cells = [];
  for (let i = 0; i < rowCount; i++) {
    cells.push(
      <div key={`l${i}`} style={{ minWidth: 0, paddingRight: '1.25rem', borderRight: `1px solid ${T.gray200}` }}>
        {ft_renderBlock(L[i], contentType, mobile, `l${i}`)}
      </div>
    );
    cells.push(
      <div key={`r${i}`} style={{ minWidth: 0, paddingLeft: '1.25rem', direction: isRTL ? 'rtl' : 'ltr' }}>
        {/* The translation column is the one read aloud. */}
        <ReadableBlock index={i} reader={reader}>
          {ft_renderBlock(R[i], contentType, mobile, `r${i}`)}
        </ReadableBlock>
      </div>
    );
  }
  return (
    <div className="formatted-text" style={{
      overflowWrap: 'anywhere',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      alignItems: 'start',
    }}>
      {cells}
    </div>
  );
}

// ─── Shared Footer ──────────────────────────────────────────────
const FOOTER_LABELS = {
  en: { allRights: 'All rights reserved.', privacy: 'Privacy Policy', terms: 'Terms of Service' },
  es: { allRights: 'Todos los derechos reservados.', privacy: 'Política de Privacidad', terms: 'Términos de Servicio' },
  fr: { allRights: 'Tous droits réservés.', privacy: 'Politique de Confidentialité', terms: "Conditions d'Utilisation" },
  zh: { allRights: '版权所有。', privacy: '隐私政策', terms: '服务条款' },
  vi: { allRights: 'Bảo lưu mọi quyền.', privacy: 'Chính Sách Bảo Mật', terms: 'Điều Khoản Dịch Vụ' },
  ru: { allRights: 'Все права защищены.', privacy: 'Политика Конфиденциальности', terms: 'Условия Использования' },
  ar: { allRights: 'جميع الحقوق محفوظة.', privacy: 'سياسة الخصوصية', terms: 'شروط الخدمة' },
  ht: { allRights: 'Tout dwa rezève.', privacy: 'Règ Konfidansyalite', terms: 'Kondisyon Sèvis' },
  pt: { allRights: 'Todos os direitos reservados.', privacy: 'Política de Privacidade', terms: 'Termos de Serviço' },
  it: { allRights: 'Tutti i diritti riservati.', privacy: 'Informativa sulla Privacy', terms: 'Termini di Servizio' },
  el: { allRights: 'Με επιφύλαξη παντός δικαιώματος.', privacy: 'Πολιτική Απορρήτου', terms: 'Όροι Χρήσης' },
};

function TranslateFooter({ lang }) {
  const fl = FOOTER_LABELS[lang] || FOOTER_LABELS.en;
  const mobile = useIsMobile();
  return (
    <footer style={{ background: T.gray900, color: T.white, padding: mobile ? '1.25rem 0' : '2rem 0', fontFamily: T.font }}>
      <div style={{
        maxWidth: '1200px', margin: '0 auto', padding: '0 1rem',
        display: 'flex', flexDirection: mobile ? 'column' : 'row',
        flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
        gap: mobile ? '0.5rem' : '1rem',
      }}>
        <Brand theme="dark" markClassName="text-blue-600"
            style={{ fontWeight: 600, fontSize: '0.95rem' }} />
        <div style={{ fontSize: mobile ? '0.78rem' : '0.875rem', color: '#d1d5db', textAlign: 'center', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: mobile ? '0.25rem 0.5rem' : '0' }}>
          <span>© 2026 HousingAI. {fl.allRights}</span>
          {!mobile && <span style={{ margin: '0 0.5rem' }}>|</span>}
          <span style={{ display: 'flex', gap: '0.5rem' }}>
            <a href="https://gethousingai.com/privacy" style={{ color: '#d1d5db', textDecoration: 'none', transition: 'color 0.15s ease' }} onMouseOver={e => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.textDecoration = 'underline'; }} onMouseOut={e => { e.currentTarget.style.color = '#d1d5db'; e.currentTarget.style.textDecoration = 'none'; }}>{fl.privacy}</a>
            <span>|</span>
            <a href="https://gethousingai.com/terms" style={{ color: '#d1d5db', textDecoration: 'none', transition: 'color 0.15s ease' }} onMouseOver={e => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.textDecoration = 'underline'; }} onMouseOut={e => { e.currentTarget.style.color = '#d1d5db'; e.currentTarget.style.textDecoration = 'none'; }}>{fl.terms}</a>
          </span>
        </div>
      </div>
   </footer>
  );
}

// ─── Language Picker ────────────────────────────────────────────
function LanguagePicker({ options, currentLang, onChange }) {
  const mobile = useIsMobile();
  return (
    <div style={{
      display: 'flex',
      gap: '0.35rem',
      flexWrap: mobile ? 'nowrap' : 'wrap',
      justifyContent: mobile ? 'flex-start' : 'center',
      overflowX: mobile ? 'auto' : 'visible',
      WebkitOverflowScrolling: 'touch',
      maxWidth: '100%',
      paddingBottom: mobile ? '4px' : 0,
    }}>
      {options.map(code => {
        const isActive = code === currentLang;
        return (
          <button
            key={code}
            onClick={() => onChange(code)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: mobile ? '0.25rem 0.55rem' : '0.3rem 0.65rem',
              fontSize: mobile ? '0.75rem' : '0.82rem',
              border: isActive ? `2px solid ${T.blue600}` : `1px solid ${T.gray200}`,
              borderRadius: T.radiusPill,
              cursor: 'pointer',
              background: isActive ? T.blue50 : T.white,
              color: isActive ? T.blue600 : T.gray500,
              fontWeight: isActive ? 700 : 400,
              transition: 'all 0.2s ease',
              fontFamily: T.font,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <FlagImg lang={code} size={mobile ? 14 : 16} /> {LANGUAGE_NAMES[code]}
          </button>
        );
      })}
    </div>
  );
}

// ─── Sticky Header ──────────────────────────────────────────────
function StickyHeader({ org, labels, languageOptions, currentLang, onLanguageChange, children, bottomContent }) {
  const mobile = useIsMobile();
  return (
    <header style={{
      background: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderBottom: `1px solid ${T.gray200}`,
      padding: mobile ? '0.75rem 1rem' : '1.25rem 1.5rem',
      position: 'sticky',
      top: 0,
      zIndex: 50,
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: mobile ? '0.5rem' : '0.75rem',
        maxWidth: '1400px',
        margin: '0 auto',
      }}>
        {/* Row 1: Back button + branding */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          justifyContent: mobile ? 'flex-start' : 'center',
          position: 'relative',
        }}>
          {!mobile && <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }}>{children}</div>}
          {mobile && children}
          <Brand as="a" href="https://gethousingai.com" target="_blank" rel="noopener noreferrer" theme="light" markClassName="text-blue-600"
            style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '1.1rem' : '1.35rem', fontFamily: T.font, textDecoration: 'none' }} />
          {!mobile && (
            <>
              <span style={{ color: '#d1d5db', fontSize: '1.5rem', fontWeight: 300 }}>|</span>
              <span style={{
                fontWeight: 700,
                color: T.gray900,
                fontSize: '1rem',
                fontFamily: T.font,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
              }}>
                <img src={`/${org}-logo.${org === 'marblehead' ? 'svg' : org === 'pittsfield' ? 'webp' : org === 'somerville' ? 'jpg' : 'png'}`} alt="" style={{ height: '28px', width: '28px', objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }} /> {formatOrgName(org)} {labels.housingAuthority}
              </span>
              <span style={{
                fontSize: '0.85rem',
                padding: '0.25rem 0.75rem',
                background: T.blue100,
                color: T.blue600,
                borderRadius: T.radiusPill,
                fontWeight: 600,
                fontFamily: T.font,
              }}>
                {labels.aiTranslation}
              </span>
            </>
          )}
        </div>

        {/* Row 2 (mobile only): Org name + badge */}
        {mobile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
          }}>
            <span style={{
              fontWeight: 700,
              color: T.gray900,
              fontSize: '0.85rem',
              fontFamily: T.font,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem',
            }}>
              <img src={`/${org}-logo.${org === 'marblehead' ? 'svg' : org === 'pittsfield' ? 'webp' : org === 'somerville' ? 'jpg' : 'png'}`} alt="" style={{ height: '22px', width: '22px', objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }} />
              {formatOrgName(org)} {labels.housingAuthority}
            </span>
            <span style={{
              fontSize: '0.72rem',
              padding: '0.2rem 0.55rem',
              background: T.blue100,
              color: T.blue600,
              borderRadius: T.radiusPill,
              fontWeight: 600,
              fontFamily: T.font,
            }}>
              {labels.aiTranslation}
            </span>
          </div>
        )}

        {/* Language picker row */}
        <LanguagePicker
          options={languageOptions}
          currentLang={currentLang}
          onChange={onLanguageChange}
        />
        {bottomContent}
      </div>
    </header>
  );
}

// ─── Page List View ─────────────────────────────────────────────
const SEARCH_PLACEHOLDERS = {
  en: 'Search documents…', es: 'Buscar documentos…', fr: 'Rechercher…',
  zh: '搜索文件…', vi: 'Tìm kiếm…', ru: 'Поиск…', ar: '…بحث',
  ht: 'Chèche dokiman…', pt: 'Pesquisar…', it: 'Cerca…', el: 'Αναζήτηση…',
};

function PageListView({ org, lang, pages }) {
  const navigate = useNavigate();
  const labels = getLabels(lang);
  const [search, setSearch] = useState('');
  const mobile = useIsMobile();

  const q = search.toLowerCase().trim();
  const pdf = pages.filter(p => p.contentType === 'pdf' && (!q || p.pageTitle?.toLowerCase().includes(q)));
  const html = pages.filter(p => p.contentType === 'html' && (!q || p.pageTitle?.toLowerCase().includes(q)));

  const handlePageClick = (page) => {
    // An official translation lives under its own language: picking the
    // Spanish CHAMP from the English (or Chinese) list jumps to Spanish.
    const target = page.sourceLang && page.sourceLang !== 'en' ? page.sourceLang : lang;
    trackTranslateDocView(org, target, page.url);
    navigate(`/translate/${org}/${target}?url=${encodeURIComponent(page.url)}`);
  };

  const cardHover = (e, hovering) => {
    e.currentTarget.style.borderColor = hovering ? T.blue600 : T.gray200;
    e.currentTarget.style.transform = hovering ? 'translateY(-1px)' : 'translateY(0)';
    e.currentTarget.style.boxShadow = hovering ? T.shadow : T.shadowSm;
  };

  return (
    <div style={{ maxWidth: '1024px', margin: '0 auto' }}>
      {/* Hero banner */}
      <div style={{
        textAlign: 'center',
        marginBottom: mobile ? '1.5rem' : '2.5rem',
        padding: mobile ? '1.5rem 1rem' : '2.5rem 2rem',
        background: T.gradient,
        borderRadius: T.radius,
        boxShadow: T.shadow,
      }}>
        <h2 style={{
          fontSize: mobile ? '1.35rem' : '2rem',
          fontWeight: 800,
          color: T.gray900,
          fontFamily: T.font,
          marginBottom: '0.25rem',
          letterSpacing: '-0.02em',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          <FlagImg lang={lang} size={mobile ? 20 : 24} />{' '}
          <span style={{ color: T.blue600 }}>{pdf.length + html.length}</span>{' '}
          {labels.documentsAvailable}
        </h2>
        <p style={{
          color: T.gray600,
          marginTop: '0.75rem',
          fontSize: mobile ? '0.82rem' : '0.88rem',
          maxWidth: '500px',
          margin: '0.75rem auto 0',
          lineHeight: 1.5,
        }}>
          {DISCLAIMERS[lang] || DISCLAIMERS.en}
        </p>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={SEARCH_PLACEHOLDERS[lang] || SEARCH_PLACEHOLDERS.en}
          style={{
            width: '100%',
            padding: '0.75rem 1rem 0.75rem 2.75rem',
            fontSize: '0.95rem',
            border: `1px solid ${T.gray200}`,
            borderRadius: T.radiusPill,
            outline: 'none',
            fontFamily: T.font,
            color: T.gray900,
            background: T.white,
            boxShadow: T.shadowSm,
            boxSizing: 'border-box',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          }}
          onFocus={e => { e.target.style.borderColor = T.blue600; e.target.style.boxShadow = `0 0 0 3px ${T.blue50}`; }}
          onBlur={e => { e.target.style.borderColor = T.gray200; e.target.style.boxShadow = T.shadowSm; }}
        />
        <svg style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.gray400} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        {search && (
          <button
            onClick={() => setSearch('')}
            style={{
              position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
              background: T.gray100, border: 'none', borderRadius: '50%',
              width: '22px', height: '22px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.75rem', color: T.gray500, fontWeight: 700,
            }}
          >×</button>
        )}
      </div>

      {/* No results */}
      {q && pdf.length === 0 && html.length === 0 && (
        <p style={{ textAlign: 'center', color: T.gray500, padding: '2rem 0', fontSize: '0.9rem' }}>
          {lang === 'es' ? 'No se encontraron resultados' : lang === 'fr' ? 'Aucun résultat trouvé' : lang === 'zh' ? '未找到结果' : 'No results found'}
        </p>
      )}

      {/* PDF Documents */}
      {pdf.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: 700,
            color: T.gray900,
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontFamily: T.font,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
  <FileText size={18} color="#2563eb" /> {labels.documentsForms}
            <span style={{              fontSize: '0.7rem',
              padding: '0.15rem 0.5rem',
              background: T.blue100,
              color: T.blue600,
              borderRadius: T.radiusPill,
              fontWeight: 700,
            }}>
              {pdf.length}
            </span>
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {pdf.map(page => (
              <button
                key={page.id}
                onClick={() => handlePageClick(page)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: mobile ? '0.75rem 1rem' : '1rem 1.25rem',
                  background: T.white,
                  border: `1px solid ${T.gray200}`,
                  borderRadius: T.radiusSm,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                  width: '100%',
                  fontFamily: T.font,
                  boxShadow: T.shadowSm,
                  gap: '0.5rem',
                }}
                onMouseOver={e => cardHover(e, true)}
                onMouseOut={e => cardHover(e, false)}
              >
                <span style={{ flex: 1, fontWeight: 500, color: T.gray800, fontSize: mobile ? '0.85rem' : 'inherit', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{page.pageTitle}</span>
                {page.sourceLang && page.sourceLang !== 'en' && (
                  <span style={{
                    fontSize: '0.65rem',
                    padding: '0.2rem 0.6rem',
                    background: '#dcfce7',
                    color: '#166534',
                    borderRadius: T.radiusPill,
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                  }}>
                    <FlagImg lang={page.sourceLang} size={12} /> {officialLabel(page.sourceLang)}
                  </span>
                )}
                <span style={{
                  fontSize: '0.65rem',
                  padding: '0.2rem 0.6rem',
                  background: '#fef3c7',
                  color: T.amber800,
                  borderRadius: T.radiusPill,
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                }}>
                  PDF
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* HTML Pages */}
      {html.length > 0 && (
        <div>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: 700,
            color: T.gray900,
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontFamily: T.font,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            <Globe size={18} color="#2563eb" /> {labels.websitePages}
            <span style={{
              fontSize: '0.7rem',
              padding: '0.15rem 0.5rem',
              background: T.blue100,
              color: T.blue600,
              borderRadius: T.radiusPill,
              fontWeight: 700,
            }}>
              {html.length}
            </span>
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {html.map(page => (
              <button
                key={page.id}
                onClick={() => handlePageClick(page)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: mobile ? '0.75rem 1rem' : '1rem 1.25rem',
                  background: T.white,
                  border: `1px solid ${T.gray200}`,
                  borderRadius: T.radiusSm,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                  width: '100%',
                  fontFamily: T.font,
                  boxShadow: T.shadowSm,
                  gap: '0.5rem',
                }}
                onMouseOver={e => cardHover(e, true)}
                onMouseOut={e => cardHover(e, false)}
              >
                <span style={{ flex: 1, fontWeight: 500, color: T.gray800, fontSize: mobile ? '0.85rem' : 'inherit', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{page.pageTitle}</span>
                {page.sourceLang && page.sourceLang !== 'en' && (
                  <span style={{
                    fontSize: '0.65rem',
                    padding: '0.2rem 0.6rem',
                    background: '#dcfce7',
                    color: '#166534',
                    borderRadius: T.radiusPill,
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                  }}>
                    <FlagImg lang={page.sourceLang} size={12} /> {officialLabel(page.sourceLang)}
                  </span>
                )}
                <span style={{
                  fontSize: '0.85rem',
                  color: T.blue600,
                  fontWeight: 600,
                }}>→</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Read-aloud bar ─────────────────────────────────────────────
// Floating player shown while read-aloud is open. Controls stay LTR in every
// language (media-player convention); the hint text follows its own script.
// The equalizer animates transform only (compositor-driven, cheap on phones)
// and holds still for anyone with reduced motion turned on.
const RA_CSS = `
.ra-pickable { cursor: pointer; border-radius: 4px; }
@media (hover: hover) {
  .ra-pickable:hover:not([aria-current]) { background: ${T.blue50}; box-shadow: 0 0 0 6px ${T.blue50}; }
}
.ra-eq { display: inline-flex; align-items: center; gap: 2px; height: 14px; flex-shrink: 0; }
.ra-eq span {
  width: 3px; height: 100%; border-radius: 2px; background: currentColor;
  transform: scaleY(0.4); transform-origin: center;
}
.ra-eq.on span { animation: ra-eq 1s ease-in-out infinite; }
.ra-eq.on span:nth-child(2) { animation-duration: 0.8s; animation-delay: -0.3s; }
.ra-eq.on span:nth-child(3) { animation-duration: 1.15s; animation-delay: -0.6s; }
.ra-eq.on span:nth-child(4) { animation-duration: 0.9s; animation-delay: -0.15s; }
@keyframes ra-eq { 0%, 100% { transform: scaleY(0.3); } 50% { transform: scaleY(1); } }
@media (prefers-reduced-motion: reduce) {
  .ra-eq.on span { animation: none; transform: scaleY(0.75); }
}
`;

// Languages no browser or OS ships a voice for (checked against Edge's full
// online catalog, Apple and Google). The Listen button stays hidden for these
// unless a device turns out to have one.
const RA_NO_PLATFORM_VOICES = new Set(['ht']);

function EqBars({ on }) {
  return (
    <span className={on ? 'ra-eq on' : 'ra-eq'} aria-hidden="true">
      <span /><span /><span /><span />
    </span>
  );
}

function ReadAloudBar({ ra, labels, readLang, mobile }) {
  const iconBtn = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: 'none',
    background: 'transparent',
    color: T.gray700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: 0,
  };
  const nextRate = READ_ALOUD_RATES[(READ_ALOUD_RATES.indexOf(ra.rate) + 1) % READ_ALOUD_RATES.length];
  const playLabel = ra.playing ? labels.pause : ra.status === 'paused' ? labels.resume : labels.play;
  return (
    <div
      role="region"
      aria-label={labels.listen}
      dir="ltr"
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
        width: mobile ? 'calc(100% - 32px)' : '400px',
        boxSizing: 'border-box',
        zIndex: 45,
        background: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: `1px solid ${T.gray200}`,
        borderRadius: T.radius,
        boxShadow: T.shadow,
        padding: '0.5rem 0.75rem 0.55rem',
        fontFamily: T.font,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.25rem' }}>
        <span style={{ width: '48px', display: 'flex', alignItems: 'center', gap: '0.4rem', color: T.blue600 }}>
          <FlagImg lang={readLang} size={22} />
          {ra.playing && <EqBars on />}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <button onClick={ra.prev} aria-label={labels.prev} title={labels.prev} style={iconBtn}>
            <SkipBack size={20} />
          </button>
          <button
            onClick={ra.playing ? ra.pause : ra.resume}
            aria-label={playLabel}
            title={playLabel}
            style={{ ...iconBtn, width: '48px', height: '48px', background: T.blue600, color: T.white, boxShadow: T.shadowSm }}
          >
            {ra.playing ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" style={{ marginLeft: 2 }} />}
          </button>
          <button onClick={ra.next} aria-label={labels.next} title={labels.next} style={iconBtn}>
            <SkipForward size={20} />
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            onClick={() => ra.setRate(nextRate)}
            aria-label={`${labels.speed}: ${ra.rate}×`}
            title={labels.speed}
            style={{ ...iconBtn, width: 'auto', minWidth: '40px', borderRadius: T.radiusPill, padding: '0 0.4rem', fontSize: '0.8rem', fontWeight: 700, fontFamily: T.font }}
          >
            {ra.rate}×
          </button>
          <button onClick={ra.stop} aria-label={labels.close} title={labels.close} style={iconBtn}>
            <X size={20} />
          </button>
        </div>
      </div>
      <p dir="auto" style={{
        margin: '0.3rem 0 0',
        fontSize: '0.74rem',
        lineHeight: 1.35,
        textAlign: 'center',
        color: ra.status === 'error' ? T.amber800 : T.gray500,
      }}>
        {ra.status === 'error' ? labels.voiceError : labels.tapHint}
      </p>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────
export default function TranslateViewerPage() {
  const { org, lang } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pageUrl = searchParams.get('url') || searchParams.get('doc');

  const labels = getLabels(lang);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [pageList, setPageList] = useState(null);
  const [sideBySide, setSideBySide] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [orgAvailableLangs, setOrgAvailableLangs] = useState([]);

  const mobile = useIsMobile();

  // Mobile bilingual tab toggle with scroll position preservation
  const [mobileTab, setMobileTab] = useState('translated'); // 'en' | 'translated'
  const scrollPctRef = useRef(0);

  const handleMobileTabSwitch = (tab) => {
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    scrollPctRef.current = maxScroll > 0 ? window.scrollY / maxScroll : 0;
    setMobileTab(tab);
    requestAnimationFrame(() => {
      const newMax = document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo(0, scrollPctRef.current * newMax);
    });
  };

  const fetchOrgLanguages = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/translate/${org}/languages`);
      if (res.ok) {
        const data = await res.json();
        setOrgAvailableLangs(data.languages || []);
      }
    } catch (err) {
      console.error('Failed to fetch org languages:', err);
    }
  }, [org]);

  const fetchPageList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/translate/${org}/pages?lang=${lang}`);
      if (!res.ok) throw new Error(`Failed to load pages (${res.status})`);
      const data = await res.json();
      setPageList(data.pages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [org, lang]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/translate/${org}?url=${encodeURIComponent(pageUrl)}&lang=${lang}`
      );
      if (!res.ok) {
        if (res.status === 404) {
          // An official translation requested under the wrong language: the
          // API says which language it belongs to, so go there.
          const body = await res.json().catch(() => ({}));
          if (body.sourceLang && body.sourceLang !== lang) {
            navigate(`/translate/${org}/${body.sourceLang}?url=${encodeURIComponent(pageUrl)}`, { replace: true });
            return;
          }
          throw new Error('Translation not available for this page.');
        }
        throw new Error(`Failed to load translation (${res.status})`);
      }
      const data = await res.json();
      setPageData(data);
      setAvailableLanguages(data.availableLanguages || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [org, lang, pageUrl]);

  useEffect(() => { fetchOrgLanguages(); }, [fetchOrgLanguages]);

  useEffect(() => {
    if (pageUrl) fetchPage();
    else fetchPageList();
  }, [pageUrl, fetchPage, fetchPageList]);

  const handleLanguageChange = (newLang) => {
    if (pageUrl) navigate(`/translate/${org}/${newLang}?url=${encodeURIComponent(pageUrl)}`);
    else navigate(`/translate/${org}/${newLang}`);
  };

  const getLanguageOptions = () => {
    let options;
    if (pageUrl && availableLanguages.length > 0) {
      options = availableLanguages.map(al => al.language);
    } else if (orgAvailableLangs.length > 0) {
      options = orgAvailableLangs.map(l => l.code);
    } else {
      options = [lang];
    }
    if (!options.includes('en')) options = ['en', ...options];
    // Always display pills in the fixed LANGUAGE_NAMES order so positions
    // never shift when switching languages or entering a document
    const order = Object.keys(LANGUAGE_NAMES);
    return [...new Set(options)].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  };

  const languageOptions = getLanguageOptions();

  // Official translation published by the authority: the right pane is the
  // authority's own text, and the English pane exists only when the API
  // paired it with an English original.
  const isOfficial = !!pageData?.official;
  const hasOriginal = !pageUrl || !!pageData?.originalText;
  // Side-by-side is actually on screen only when there are two texts to pair.
  const sbsActive = !mobile && sideBySide && hasOriginal && lang !== 'en';

  // ─── Read aloud ────────────────────────────────────────────
  // Reads the text on screen: the translation (the right column in
  // side-by-side), or English on English pages and the phone's English tab.
  const readingEnglish = lang === 'en' || (mobile && mobileTab === 'en' && hasOriginal);
  const readLang = readingEnglish ? 'en' : lang;
  // pageUrl too: going back to the list keeps the last pageData in state, and
  // an empty text is what ends the read-aloud session.
  const readText = pageUrl && pageData ? (readingEnglish ? pageData.originalText : pageData.translatedText) : '';
  const readContentType = pageData?.contentType || 'pdf';
  const readChunks = useMemo(
    () => ft_blocks(readText || '').map(b =>
      ft_speechLines(b, readContentType).flatMap(line => splitForSpeech(line, readLang))),
    [readText, readContentType, readLang],
  );
  const ra = useReadAloud(readLang, readChunks);
  const raLabels = READ_ALOUD_LABELS[lang] || READ_ALOUD_LABELS.en;
  const platform = useMemo(detectPlatform, []);
  const [showVoiceHelp, setShowVoiceHelp] = useState(false);
  const voiceName = ra.voice ? ra.voice.name : null;

  // The header button doubles as play/pause once a session is open; the bar's
  // X is what ends it. Pause rather than stop keeps the reader's place.
  const listenMode = ra.playing ? 'pause' : ra.status === 'paused' ? 'play' : 'listen';
  const showListen = ra.supported && (!!ra.voice || !RA_NO_PLATFORM_VOICES.has(readLang));
  const handleListen = () => {
    if (listenMode === 'pause') { ra.pause(); return; }
    if (listenMode === 'play') { ra.resume(); return; }
    if (!ra.voice) { setShowVoiceHelp(v => !v); return; }
    trackReadAloudStart(org, readLang, 'page', voiceName);
    ra.start();
  };
  const reader = {
    active: ra.active,
    current: ra.current,
    speakable: readChunks.map(c => c.length > 0),
    onPick: (i) => {
      trackReadAloudStart(org, readLang, 'paragraph', voiceName);
      ra.playFrom(i);
    },
  };

  // Once per document and reading language: does this device have a voice?
  const availKey = pageData && ra.supported && ra.voicesSettled ? `${pageUrl}|${readLang}` : null;
  const trackedAvailRef = useRef(null);
  useEffect(() => {
    setShowVoiceHelp(false);
    if (!availKey || trackedAvailRef.current === availKey) return;
    trackedAvailRef.current = availKey;
    trackReadAloudAvailability(org, readLang, platform, voiceName);
  }, [availKey, org, readLang, platform, voiceName]);

  // Keep the paragraph being read on screen, unless the reader has just
  // scrolled somewhere else themselves.
  const userScrollRef = useRef(0);
  useEffect(() => {
    const mark = () => { userScrollRef.current = Date.now(); };
    window.addEventListener('wheel', mark, { passive: true });
    window.addEventListener('touchmove', mark, { passive: true });
    return () => {
      window.removeEventListener('wheel', mark);
      window.removeEventListener('touchmove', mark);
    };
  }, []);
  const raCurrent = ra.current;
  const raPlaying = ra.playing;
  useEffect(() => {
    if (raCurrent == null || !raPlaying) return;
    if (Date.now() - userScrollRef.current < 4000) return;
    const el = document.querySelector(`[data-ra-block="${raCurrent}"]`);
    if (!el) return;
    const r = el.getBoundingClientRect();
    const top = 150;                          // below the sticky header
    const bottom = window.innerHeight - 120;  // above the read-aloud bar
    if (r.top >= top && r.bottom <= bottom) return;
    const offset = r.height < bottom - top ? top + (bottom - top - r.height) / 2 : top;
    window.scrollTo({ top: Math.max(0, window.scrollY + r.top - offset), behavior: 'smooth' });
  }, [raCurrent, raPlaying]);

  // ─── Loading ───────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontFamily: T.font,
        background: T.gradient,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '44px',
            height: '44px',
            border: `3px solid ${T.gray200}`,
            borderTopColor: T.blue600,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 1.25rem',
          }} />
          <p style={{ color: T.gray600, fontWeight: 500 }}>{labels.loading}</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ─── Error ─────────────────────────────────────────────────
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontFamily: T.font,
        background: T.gradient,
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2.5rem',
          background: T.white,
          borderRadius: T.radius,
          maxWidth: '500px',
          boxShadow: T.shadow,
        }}>
          <div style={{ marginBottom: '1rem' }}><AlertTriangle size={40} color="#b45309" /></div>

          <p style={{ color: T.gray900, fontWeight: 700, marginBottom: '0.5rem', fontSize: '1.1rem' }}>
            {labels.errorTitle}
          </p>
          <p style={{ color: T.gray600, fontSize: '0.9rem', marginBottom: '1.5rem' }}>{error}</p>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: '0.65rem 2rem',
              background: T.blue600,
              color: T.white,
              border: 'none',
              borderRadius: T.radiusPill,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.9rem',
              fontFamily: T.font,
              transition: 'all 0.2s ease',
            }}
          >
            {labels.goBack}
          </button>
        </div>
      </div>
    );
  }

  // ─── Page Listing ──────────────────────────────────────────
  if (!pageUrl && pageList) {
    return (
      <div style={{
        fontFamily: T.font,
        minHeight: '100vh',
        background: T.gradient,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <StickyHeader
          org={org}
          labels={labels}
          languageOptions={languageOptions}
          currentLang={lang}
          onLanguageChange={handleLanguageChange}
        >
          <button
            onClick={() => navigate('/translate', { state: { lang } })}
            style={{
              background: T.gray100,
              border: `1px solid ${T.gray200}`,
              borderRadius: T.radiusPill,
              cursor: 'pointer',
              fontSize: '0.85rem',
              color: T.gray600,
              padding: '0.45rem 0.85rem',
              transition: 'all 0.15s ease',
              fontFamily: T.font,
              fontWeight: 600,
            }}
            title="All Housing Authorities"
            onMouseOver={e => { e.currentTarget.style.background = T.blue50; e.currentTarget.style.borderColor = T.blue600; e.currentTarget.style.color = T.blue600; }}
            onMouseOut={e => { e.currentTarget.style.background = T.gray100; e.currentTarget.style.borderColor = T.gray200; e.currentTarget.style.color = T.gray600; }}
          >
            ←
          </button>
        </StickyHeader>

        <main style={{ padding: mobile ? '1.25rem 0.75rem' : '2.5rem 1.5rem', flex: 1 }}>
          <PageListView org={org} lang={lang} pages={pageList} />
        </main>

        <TranslateFooter lang={lang} />
        <ScrollToTopButton />
      </div>
    );
  }

  // ─── Single Page View ─────────────────────────────────────
  if (!pageData) return null;
  const isRTL = lang === 'ar';

  return (
    <div style={{
      fontFamily: T.font,
      minHeight: '100vh',
      background: T.gradient,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <StickyHeader
        org={org}
        labels={labels}
        languageOptions={languageOptions}
        currentLang={lang}
        onLanguageChange={handleLanguageChange}
        bottomContent={lang !== 'en' && hasOriginal ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            paddingTop: mobile ? '0.5rem' : '0.65rem',
          }}>
            {mobile ? (
              /* Mobile: tab toggle between English / Translation */
              <div style={{
                display: 'inline-flex',
                borderRadius: T.radiusPill,
                border: `1px solid ${T.gray200}`,
                overflow: 'hidden',
                boxShadow: T.shadowSm,
              }}>
                <button
                  onClick={() => handleMobileTabSwitch('en')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    padding: '0.45rem 0.85rem',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    fontFamily: T.font,
                    border: 'none',
                    cursor: 'pointer',
                    background: mobileTab === 'en' ? T.blue600 : T.white,
                    color: mobileTab === 'en' ? T.white : T.gray500,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <FlagImg lang="en" size={14} /> {labels.englishVersion.split('(')[0].trim()}
                </button>
                <button
                  onClick={() => handleMobileTabSwitch('translated')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    padding: '0.45rem 0.85rem',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    fontFamily: T.font,
                    border: 'none',
                    borderLeft: `1px solid ${T.gray200}`,
                    cursor: 'pointer',
                    background: mobileTab === 'translated' ? T.blue600 : T.white,
                    color: mobileTab === 'translated' ? T.white : T.gray500,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <FlagImg lang={lang} size={14} /> {LANGUAGE_NAMES[lang]}
                </button>
              </div>
            ) : (
              /* Desktop: side-by-side toggle */
              <button
                onClick={() => setSideBySide(!sideBySide)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1.25rem',
                  border: sideBySide ? `2px solid ${T.blue600}` : `1px solid ${T.gray200}`,
                  borderRadius: T.radiusPill,
                  background: sideBySide ? T.blue50 : T.white,
                  cursor: 'pointer',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: sideBySide ? T.blue600 : T.gray600,
                  transition: 'all 0.2s ease',
                  fontFamily: T.font,
                  boxShadow: T.shadowSm,
                }}
              >
                <BookOpen size={16} />
                {sideBySide ? labels.sideBySideOn : labels.sideBySide}
              </button>
            )}
          </div>
        ) : null}
      >
        <button
          onClick={() => navigate(`/translate/${org}/${lang}`)}
          style={{
            background: T.gray100,
            border: `1px solid ${T.gray200}`,
            borderRadius: T.radiusPill,
            cursor: 'pointer',
            fontSize: '0.85rem',
            color: T.gray600,
            padding: '0.45rem 0.85rem',
            transition: 'all 0.15s ease',
            fontFamily: T.font,
            fontWeight: 600,
          }}
          title={labels.backToAll}
          onMouseOver={e => { e.currentTarget.style.background = T.blue50; e.currentTarget.style.borderColor = T.blue600; e.currentTarget.style.color = T.blue600; }}
          onMouseOut={e => { e.currentTarget.style.background = T.gray100; e.currentTarget.style.borderColor = T.gray200; e.currentTarget.style.color = T.gray600; }}
        >
          ←
        </button>
      </StickyHeader>

      {/* Page Title Area */}
      <div style={{
        padding: mobile ? '1rem 0.75rem 0' : '1.5rem 1.5rem 0',
        maxWidth: sbsActive ? '1400px' : '1024px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
      }}>
        <h1 style={{
          fontSize: mobile ? '1.25rem' : '1.75rem',
          fontWeight: 800,
          color: T.gray900,
          fontFamily: T.font,
          marginBottom: '0.5rem',
          letterSpacing: '-0.02em',
        }}>
          {pageData.pageTitle || 'Untitled Page'}
        </h1>
        <div style={{
          display: 'flex',
          gap: mobile ? '0.5rem' : '0.75rem',
          alignItems: mobile ? 'flex-start' : 'center',
          flexWrap: 'wrap',
          fontSize: mobile ? '0.78rem' : '0.82rem',
          color: T.gray500,
          marginBottom: '1.25rem',
        }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            padding: '0.2rem 0.65rem',
            background: T.blue50,
            borderRadius: T.radiusPill,
            fontWeight: 600,
            color: T.blue600,
          }}>
            <FlagImg lang={lang} size={16} /> {LANGUAGE_NAMES[lang]}
          </span>
          {isOfficial && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.2rem 0.65rem',
              background: '#dcfce7',
              color: '#166534',
              borderRadius: T.radiusPill,
              fontWeight: 700,
            }}>
              {officialLabel(lang)}
            </span>
          )}
          {pageData.contentType === 'pdf' && (
            <span style={{
              padding: '0.2rem 0.6rem',
              background: '#fef3c7',
              color: T.amber800,
              borderRadius: T.radiusPill,
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.05em',
            }}>
              PDF
            </span>
          )}
          <a
            href={pageData.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: T.blue600,
              textDecoration: 'none',
              fontWeight: 600,
              transition: 'opacity 0.15s ease',
            }}
          >
            {labels.viewOriginal} ↗
          </a>
          {showListen && (
            <button
              onClick={handleListen}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                marginLeft: 'auto',
                padding: '0.3rem 0.85rem',
                border: `1px solid ${ra.voice ? T.blue600 : T.gray200}`,
                borderRadius: T.radiusPill,
                background: listenMode === 'listen' ? T.white : T.blue600,
                color: listenMode !== 'listen' ? T.white : ra.voice ? T.blue600 : T.gray400,
                fontWeight: 700,
                fontSize: mobile ? '0.78rem' : '0.82rem',
                fontFamily: T.font,
                cursor: 'pointer',
                transition: 'background 0.2s ease, color 0.2s ease',
              }}
            >
              {listenMode === 'pause' && <><EqBars on /> {raLabels.pause}</>}
              {listenMode === 'play' && <><Play size={14} fill="currentColor" /> {raLabels.resume}</>}
              {listenMode === 'listen' && <><Volume2 size={15} /> {raLabels.listen}</>}
            </button>
          )}
        </div>
        {showVoiceHelp && !ra.voice && (
          <p dir="auto" style={{
            fontSize: '0.8rem',
            color: T.amber800,
            background: T.amber50,
            border: `1px solid ${T.amber200}`,
            borderRadius: T.radiusSm,
            padding: '0.55rem 0.8rem',
            marginBottom: '0.75rem',
            lineHeight: 1.45,
          }}>
            {raLabels.noVoice}
            {platform.os === 'windows' && ` ${raLabels.noVoiceWindows}`}
            {platform.os === 'android' && ` ${raLabels.noVoiceAndroid}`}
          </p>
        )}
        <p style={{
          fontSize: '0.78rem',
          color: T.gray500,
          fontStyle: 'italic',
          lineHeight: 1.4,
          marginBottom: '0.5rem',
        }}>
          {labels.contentDisclaimer}
        </p>
      </div>

      {/* Content */}
      <main style={{
        padding: mobile ? '0 0.75rem 1.5rem' : '0 1.5rem 2.5rem',
        maxWidth: sbsActive ? '1400px' : '1024px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
        flex: 1,
      }}>
        {/* English has nothing to pair with: never render it beside itself,
            whatever the toggle said in the previous language. */}
        {sbsActive ? (
          <div style={{
            background: T.white,
            border: `1px solid ${T.gray200}`,
            borderRadius: T.radius,
            padding: '2rem',
            boxShadow: T.shadowSm,
          }}>
            {/* Column headers — the content below is rendered as PAIRED ROWS so
                the two languages stay aligned while scrolling (#11). */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              marginBottom: '1.25rem',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                paddingRight: '1.25rem',
                paddingBottom: '0.75rem',
                borderBottom: `2px solid ${T.gray200}`,
              }}>
                <FlagImg lang="en" size={22} />
                <span style={{ fontWeight: 700, color: T.gray900, fontSize: '0.9rem' }}>
                  {labels.englishVersion}
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                paddingLeft: '1.25rem',
                paddingBottom: '0.75rem',
                borderBottom: `2px solid ${T.blue100}`,
              }}>
                <FlagImg lang={lang} size={22} />
                <span style={{ fontWeight: 700, color: T.blue600, fontSize: '0.9rem' }}>
                  {LANGUAGE_NAMES[lang]} ({isOfficial ? officialLabel(lang) : labels.aiVersion})
                </span>
              </div>
            </div>

            <PairedText
              leftText={pageData.originalText}
              rightText={pageData.translatedText}
              contentType={pageData.contentType}
              isRTL={isRTL}
              reader={reader}
            />
          </div>
        ) : (
          <div style={{
            background: T.white,
            border: `1px solid ${T.gray200}`,
            borderRadius: T.radius,
            padding: mobile ? '1.25rem' : '2.5rem',
            direction: isRTL && !(mobile && mobileTab === 'en') ? 'rtl' : 'ltr',
            boxShadow: T.shadow,
          }}>
            {/* On mobile for non-English: show header indicating which version */}
            {mobile && lang !== 'en' && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1rem',
                paddingBottom: '0.65rem',
                borderBottom: `2px solid ${mobileTab === 'en' && hasOriginal ? T.gray200 : T.blue100}`,
              }}>
                <FlagImg lang={mobileTab === 'en' && hasOriginal ? 'en' : lang} size={18} />
                <span style={{ fontWeight: 700, color: mobileTab === 'en' && hasOriginal ? T.gray900 : T.blue600, fontSize: '0.85rem' }}>
                  {mobileTab === 'en' && hasOriginal ? labels.englishVersion : `${LANGUAGE_NAMES[lang]} (${isOfficial ? officialLabel(lang) : labels.aiVersion})`}
                </span>
              </div>
            )}
            <FormattedText
              text={
                lang === 'en'
                  ? pageData.originalText
                  : mobile
                    ? (mobileTab === 'en' && hasOriginal ? pageData.originalText : pageData.translatedText)
                    : pageData.translatedText
              }
              contentType={pageData.contentType}
              reader={reader}
            />
          </div>
        )}

        {/* Disclaimer — shown for ALL languages including English */}
        <div style={{
          marginTop: mobile ? '1rem' : '1.5rem',
          padding: mobile ? '1rem' : '1.25rem 1.5rem',
          background: T.amber50,
          border: `1px solid ${T.amber200}`,
          borderRadius: T.radiusSm,
          fontSize: '0.82rem',
          lineHeight: 1.6,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
          }}>
            <AlertTriangle size={18} color="#b45309" style={{ flexShrink: 0, marginTop: '0.1rem' }} />
            <div>
              <p style={{ color: T.amber800, fontWeight: 700, marginBottom: '0.35rem' }}>
                {labels.importantNotice}
              </p>
              <p style={{ color: T.amber900 }}>
                {DISCLAIMERS[lang] || DISCLAIMERS.en}
              </p>
              <div style={{ marginTop: '0.85rem' }}>
                <a
                  href={pageData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.45rem 1rem',
                    background: T.navy,
                    color: T.white,
                    borderRadius: T.radiusPill,
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    transition: 'opacity 0.15s ease',
                  }}
                >
                  <FlagImg lang="en" size={14} /> {labels.officialEnglish}
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <TranslateFooter lang={lang} />
      <ScrollToTopButton raised={ra.active} />
      <style>{RA_CSS}</style>
      {ra.active && <ReadAloudBar ra={ra} labels={raLabels} readLang={readLang} mobile={mobile} />}
    </div>
  );
}